// eslint-disable-next-line import/no-extraneous-dependencies
import axios from 'axios';
import configService from '@/services/remoteConfig';

// Cloud Functions API base URL
const baseURL = configService.get('apiBaseUrl');
const apiClient = axios.create({
  baseURL,
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  },
  timeout: 10000, // 10 second timeout
});

// Token storage keys
const REQUEST_TOKEN_STORAGE_KEY = 'discogs_request_token';

/**
 * Store token data in localStorage
 * @param {string} requestToken - OAuth request token
 * @param {string} requestTokenSecret - OAuth request token secret
 */
const storeRequestTokenData = (requestToken, requestTokenSecret) => {
  try {
    const tokenData = { requestToken, requestTokenSecret };
    localStorage.setItem(REQUEST_TOKEN_STORAGE_KEY, JSON.stringify(tokenData));
    console.log('auth.js: Stored request token data successfully');
  } catch (error) {
    console.error('Error storing request token data:', error);
  }
};

/**
 * Get stored request token data
 * @returns {Object|null} - Request token data or null if not available
 */
const getRequestTokenData = () => {
  try {
    const data = JSON.parse(localStorage.getItem(REQUEST_TOKEN_STORAGE_KEY) || 'null');
    console.log('auth.js: Retrieved request token data:', data ? 'Found' : 'Not found');
    return data;
  } catch (error) {
    console.error('Error retrieving request token data:', error);
    return null;
  }
};

/**
 * Initiates the OAuth flow with Discogs
 * @param {string} callbackUrl - URL to redirect to after authentication
 * @returns {Promise<string>} - URL to redirect user for authentication
 */
const initiateAuth = async callbackUrl => {
  try {
    console.log('auth.js: Initializing auth with callback:', callbackUrl);

    // Validate callback URL
    if (!callbackUrl) {
      throw new Error('Callback URL is required');
    }

    // Log the full request URL for debugging
    const requestUrl = `${baseURL}/authInit?callback=${encodeURIComponent(callbackUrl)}`;
    console.log('auth.js: Making API request to:', requestUrl);

    let authData = null;

    // First try using fetch API
    try {
      console.log('auth.js: Attempting fetch request...');
      const response = await fetch(requestUrl, {
        method: 'GET',
        headers: {
          Accept: 'application/json',
        },
      });

      // Log response status
      console.log('auth.js: Fetch response status:', response.status);

      if (!response.ok) {
        console.error('Fetch API error:', response.status, response.statusText);
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      // Parse response
      const data = await response.json();
      console.log('auth.js: Fetch API response received:', JSON.stringify(data));
      authData = data;
    } catch (fetchError) {
      console.error('Fetch API failed, trying axios:', fetchError);

      // Fall back to axios if fetch fails
      try {
        console.log('auth.js: Attempting axios request...');
        const { data } = await apiClient.get(`authInit?callback=${encodeURIComponent(callbackUrl)}`);
        console.log('auth.js: Axios response received:', JSON.stringify(data));
        authData = data;
      } catch (axiosError) {
        console.error('Axios request failed:', axiosError);
        if (axiosError.response) {
          console.error('Response data:', axiosError.response.data);
          console.error('Response status:', axiosError.response.status);
        }
        throw axiosError;
      }
    }

    // Validate auth data
    if (!authData || !authData.url || !authData.requestToken || !authData.requestTokenSecret) {
      console.error('Invalid auth data received:', authData);
      throw new Error('Invalid response from authentication server');
    }

    // Store request token and secret temporarily
    storeRequestTokenData(authData.requestToken, authData.requestTokenSecret);

    // Return the authorization URL
    console.log('auth.js: Auth initialization successful, returning URL:', authData.url);
    return authData.url;
  } catch (error) {
    // Log more detailed error information
    console.error('Error initiating authentication:', error);
    if (error.response) {
      console.error('Response data:', error.response.data);
      console.error('Response status:', error.response.status);
      console.error('Response headers:', error.response.headers);
    } else if (error.request) {
      console.error('No response received:', error.request);
    } else {
      console.error('Request setup error:', error.message);
    }

    throw error;
  }
};

/**
 * Handles OAuth callback with verification code
 * @param {Object} params - OAuth callback parameters
 * @returns {Promise<Object>} - User identity data
 */
const handleCallback = async params => {
  try {
    console.log('auth.js: Handling OAuth callback with params:', params);

    // Validate params
    const oauthToken = params.oauth_token;
    const oauthVerifier = params.oauth_verifier;

    if (!oauthToken || !oauthVerifier) {
      throw new Error('Missing required OAuth parameters (token or verifier)');
    }

    // Retrieve stored request token and secret
    const storedTokenData = getRequestTokenData();

    if (!storedTokenData || !storedTokenData.requestTokenSecret) {
      console.error('Missing token data. Local storage data:', localStorage.getItem(REQUEST_TOKEN_STORAGE_KEY));
      throw new Error('Missing request token data. Your session may have expired. Please try again.');
    }

    const { requestTokenSecret } = storedTokenData;
    console.log('auth.js: Retrieved request token secret, length:', requestTokenSecret.length);

    // Build callback URL
    const callbackUrl = `authCallback?oauth_token=${encodeURIComponent(oauthToken)}&oauth_verifier=${encodeURIComponent(oauthVerifier)}&requestTokenSecret=${encodeURIComponent(requestTokenSecret)}`;
    console.log('auth.js: Making callback request to:', `${baseURL}/authCallback`);

    // Exchange request token for access token
    const { data: accessData } = await apiClient.get(callbackUrl);
    console.log('auth.js: Received access data:', JSON.stringify({
      hasAccessToken: !!accessData.accessToken,
      hasAccessTokenSecret: !!accessData.accessTokenSecret,
    }));

    // Validate response
    if (!accessData || !accessData.accessToken || !accessData.accessTokenSecret) {
      throw new Error('Invalid response from authentication server');
    }

    // Clean up request token
    localStorage.removeItem(REQUEST_TOKEN_STORAGE_KEY);
    console.log('auth.js: Cleaned up request token data');

    return {
      accessToken: accessData.accessToken,
      accessTokenSecret: accessData.accessTokenSecret,
      username: 'discogs_user', // Placeholder - will be fetched from API
    };
  } catch (error) {
    console.error('Error handling callback:', error);

    // Add more context to the error
    if (error.response) {
      console.error('Response data:', error.response.data);
      console.error('Response status:', error.response.status);
    }

    throw error;
  }
};

export {
  initiateAuth,
  handleCallback,
};
